/**
 * 性能监控工具
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {}
    this.observers = []
    this.isEnabled = process.env.NODE_ENV !== 'production' || window.location.search.includes('debug=true')
    
    if (this.isEnabled) {
      this.init()
    }
  }

  init() {
    // 监控页面加载性能
    this.monitorPageLoad()
    
    // 监控资源加载
    this.monitorResourceLoad()
    
    // 监控长任务
    this.monitorLongTasks()
    
    // 监控内存使用
    this.monitorMemory()
    
    // 监控用户交互
    this.monitorUserInteraction()
  }

  // 页面加载性能监控
  monitorPageLoad() {
    if ('performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = performance.getEntriesByType('navigation')[0]
          if (perfData) {
            this.metrics.pageLoad = {
              // DNS查询时间
              dnsLookup: perfData.domainLookupEnd - perfData.domainLookupStart,
              // TCP连接时间
              tcpConnect: perfData.connectEnd - perfData.connectStart,
              // 请求响应时间
              request: perfData.responseEnd - perfData.requestStart,
              // DOM解析时间
              domParse: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
              // 资源加载时间
              resourceLoad: perfData.loadEventEnd - perfData.domContentLoadedEventEnd,
              // 总加载时间
              totalLoad: perfData.loadEventEnd - perfData.navigationStart,
              // 首次内容绘制
              fcp: this.getFCP(),
              // 最大内容绘制
              lcp: this.getLCP()
            }
            
            this.logPerformanceMetrics()
          }
        }, 1000)
      })
    }
  }

  // 获取首次内容绘制时间
  getFCP() {
    const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0]
    return fcpEntry ? fcpEntry.startTime : null
  }

  // 获取最大内容绘制时间
  getLCP() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          resolve(lastEntry ? lastEntry.startTime : null)
        })
        observer.observe({ entryTypes: ['largest-contentful-paint'] })
        this.observers.push(observer)
        
        // 10秒后停止观察
        setTimeout(() => {
          observer.disconnect()
          resolve(null)
        }, 10000)
      } else {
        resolve(null)
      }
    })
  }

  // 资源加载监控
  monitorResourceLoad() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach(entry => {
          if (entry.initiatorType === 'img' || entry.name.includes('.png') || entry.name.includes('.jpg') || entry.name.includes('.webp')) {
            if (!this.metrics.images) this.metrics.images = []
            this.metrics.images.push({
              name: entry.name.split('/').pop(),
              size: entry.transferSize,
              loadTime: entry.duration,
              type: entry.initiatorType
            })
          }
        })
      })
      
      observer.observe({ entryTypes: ['resource'] })
      this.observers.push(observer)
    }
  }

  // 长任务监控
  monitorLongTasks() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach(entry => {
          if (!this.metrics.longTasks) this.metrics.longTasks = []
          this.metrics.longTasks.push({
            duration: entry.duration,
            startTime: entry.startTime,
            attribution: entry.attribution
          })
        })
      })
      
      try {
        observer.observe({ entryTypes: ['longtask'] })
        this.observers.push(observer)
      } catch (e) {
        // longtask可能不被支持
      }
    }
  }

  // 内存监控
  monitorMemory() {
    if ('memory' in performance) {
      setInterval(() => {
        this.metrics.memory = {
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit,
          usage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit * 100).toFixed(2)
        }
      }, 5000)
    }
  }

  // 用户交互监控
  monitorUserInteraction() {
    // 首次输入延迟
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach(entry => {
          this.metrics.fid = entry.processingStart - entry.startTime
        })
      })
      
      try {
        observer.observe({ entryTypes: ['first-input'] })
        this.observers.push(observer)
      } catch (e) {
        // first-input可能不被支持
      }
    }

    // 点击响应时间
    let clickStartTime = 0
    document.addEventListener('mousedown', () => {
      clickStartTime = performance.now()
    })
    
    document.addEventListener('mouseup', () => {
      if (clickStartTime) {
        const responseTime = performance.now() - clickStartTime
        if (!this.metrics.interactions) this.metrics.interactions = []
        this.metrics.interactions.push({
          type: 'click',
          responseTime,
          timestamp: Date.now()
        })
      }
    })
  }

  // 图片加载性能分析
  analyzeImagePerformance() {
    if (!this.metrics.images) return null

    const images = this.metrics.images
    const totalSize = images.reduce((sum, img) => sum + img.size, 0)
    const avgLoadTime = images.reduce((sum, img) => sum + img.loadTime, 0) / images.length
    
    return {
      totalImages: images.length,
      totalSize: (totalSize / 1024 / 1024).toFixed(2) + 'MB',
      avgLoadTime: avgLoadTime.toFixed(2) + 'ms',
      slowestImage: images.reduce((slowest, img) => 
        img.loadTime > slowest.loadTime ? img : slowest
      ),
      largestImage: images.reduce((largest, img) => 
        img.size > largest.size ? img : largest
      )
    }
  }

  // 生成性能报告
  generateReport() {
    const report = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      ...this.metrics
    }

    // 图片性能分析
    const imageAnalysis = this.analyzeImagePerformance()
    if (imageAnalysis) {
      report.imageAnalysis = imageAnalysis
    }

    return report
  }

  // 性能建议
  getOptimizationSuggestions() {
    const suggestions = []
    
    if (this.metrics.pageLoad) {
      const { totalLoad, fcp } = this.metrics.pageLoad
      
      if (totalLoad > 3000) {
        suggestions.push({
          type: 'warning',
          message: `页面总加载时间${(totalLoad/1000).toFixed(1)}秒过长，建议优化图片和资源`,
          priority: 'high'
        })
      }
      
      if (fcp > 1800) {
        suggestions.push({
          type: 'warning', 
          message: `首次内容绘制时间${(fcp/1000).toFixed(1)}秒过长，建议优化关键资源`,
          priority: 'medium'
        })
      }
    }

    if (this.metrics.images && this.metrics.images.length > 20) {
      // 检查是否已启用懒加载
      const hasLazyLoading = document.querySelectorAll('[v-lazy], [data-src], .lazy, [loading="lazy"]').length > 0
      if (hasLazyLoading) {
        suggestions.push({
          type: 'success',
          message: `✅ 已启用图片懒加载，当前页面${this.metrics.images.length}张图片`,
          priority: 'low'
        })
      } else {
        suggestions.push({
          type: 'info',
          message: `页面包含${this.metrics.images.length}张图片，建议使用懒加载`,
          priority: 'medium'
        })
      }
    }

    const imageAnalysis = this.analyzeImagePerformance()
    if (imageAnalysis && parseFloat(imageAnalysis.totalSize) > 5) {
      suggestions.push({
        type: 'warning',
        message: `图片总大小${imageAnalysis.totalSize}过大，建议压缩或使用WebP格式`,
        priority: 'high'
      })
    }

    if (this.metrics.memory && parseFloat(this.metrics.memory.usage) > 70) {
      suggestions.push({
        type: 'warning',
        message: `内存使用率${this.metrics.memory.usage}%过高，可能存在内存泄漏`,
        priority: 'high'
      })
    }

    return suggestions
  }

  // 控制台输出性能指标
  logPerformanceMetrics() {
    if (!this.isEnabled) return

    console.group('🚀 性能监控报告')
    
    if (this.metrics.pageLoad) {
      console.log('📊 页面加载性能:', this.metrics.pageLoad)
    }
    
    const imageAnalysis = this.analyzeImagePerformance()
    if (imageAnalysis) {
      console.log('🖼️ 图片性能分析:', imageAnalysis)
    }
    
    if (this.metrics.memory) {
      console.log('💾 内存使用:', this.metrics.memory)
    }

    const suggestions = this.getOptimizationSuggestions()
    if (suggestions.length > 0) {
      console.log('💡 优化建议:')
      suggestions.forEach(suggestion => {
        const icon = suggestion.priority === 'high' ? '🔴' : suggestion.priority === 'medium' ? '🟡' : '🔵'
        console.log(`${icon} ${suggestion.message}`)
      })
    }
    
    console.groupEnd()
  }

  // 清理监听器
  destroy() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }
}

// 创建全局实例
export const performanceMonitor = new PerformanceMonitor()

// Vue插件
export const PerformanceMonitorPlugin = {
  install(Vue) {
    Vue.prototype.$performanceMonitor = performanceMonitor
    
    // 在开发环境下添加全局方法
    if (process.env.NODE_ENV !== 'production') {
      window.showPerformanceReport = () => {
        console.log(performanceMonitor.generateReport())
      }
      
      window.getOptimizationSuggestions = () => {
        performanceMonitor.getOptimizationSuggestions().forEach(suggestion => {
          console.log(`${suggestion.priority.toUpperCase()}: ${suggestion.message}`)
        })
      }
    }
  }
}

export default performanceMonitor
